import { Component, OnInit, OnDestroy, DoCheck, AfterViewInit, ElementRef, Inject, NgZone, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { SeoService } from '../seo.service';

// ─── Translation interface ────────────────────────────────────────────────────
interface Translations {
  nav_quienes: string;
  nav_servicios: string;
  nav_trabajos: string;
  nav_contacto: string;
  nav_metodologia: string;
  hero_location: string;
  hero_line1: string;
  hero_line2: string;
  hero_desc: string;
  tele_projects: string;
  tele_years: string;
  tele_mode: string;
  tele_mode_val: string;
  cta_services: string;
  cta_works: string;
  about_label: string;
  about_title: string;
  about_p1: string;
  about_p2: string;
  card1_label: string;
  card1_value: string;
  card1_sub: string;
  card2_label: string;
  card2_value: string;
  card2_sub: string;
  services_label: string;
  services_title: string;
  services_desc: string;
  works_label: string;
  works_title: string;
  works_desc: string;
  method_label: string;
  method_title: string;
  method_desc: string;
  method_cta: string;
  faq_label: string;
  faq_title: string;
  faq_desc: string;
  visit_site: string;
  visit_more: string;
  cta_label: string;
  cta_title: string;
  cta_email: string;
  footer_sub: string;
  footer_nav: string;
  footer_services: string;
  footer_rights: string;
}

interface ServiceItem {
  icon: string;
  name_esp: string;
  name_eng: string;
  desc_esp: string;
  desc_eng: string;
  tags: string[];
  route: string;
}

interface WorkItem {
  name: string;
  desc_esp: string;
  desc_eng: string;
  tags: string[];
  url: string;
}

interface StatItem {
  num: string;
  suffix: string;
  label_esp: string;
  label_eng: string;
}

interface FaqItem {
  question_esp: string;
  question_eng: string;
  answer_esp: string;
  answer_eng: string;
}

interface CircuitZone {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

@Component({
  selector: 'ngbd-carousel-basic',
  templateUrl: './carousel-basic.component.html',
  styleUrls: ['./carousel-basic.component.scss'],
})
export class NgbdCarouselBasic implements OnInit, DoCheck, AfterViewInit, OnDestroy {

  // ─── State ─────────────────────────────────────────────────────────────────
  lang: 'ESP' | 'ENG' = 'ESP';
  t: Translations = {} as Translations;

  private circuitRaf = 0;
  // Ruta actual para los url(#id) del SVG del robot: con <base href="/"> un
  // url(#id) pelado se resuelve contra "/" y falla en /portada o /contacto.
  private svgBase = '';

  private circuitResizeHandler = () => this.sizeCircuitCanvas();

  // ─── Marquee items ──────────────────────────────────────────────────────────
  marqueeItems = [
    'Robotización', 'Desarrollo Web','E-Commerce', 'Software a Medida',
    'Automatización', 'Inteligencia Artificial', 'Mobile Apps',
    'UX & UI Design', 'Rosario, Argentina'
  ];

  // ─── Stats ──────────────────────────────────────────────────────────────────
  stats: StatItem[] = [
    { num: '25', suffix: '+', label_esp: 'Proyectos entregados', label_eng: 'Projects delivered' },
    { num: '8',  suffix: '+', label_esp: 'Años de experiencia',  label_eng: 'Years of experience' },
    { num: '5',  suffix: '',  label_esp: 'Áreas de servicio',    label_eng: 'Service areas' },
    { num: '2',  suffix: '',  label_esp: 'Idiomas de soporte',   label_eng: 'Languages supported' },
  ];

  // ─── Services ───────────────────────────────────────────────────────────────
  services: ServiceItem[] = [
    {
      icon: 'robot',
      name_esp: 'Robotización',
      name_eng: 'Robotics',
      desc_esp: 'Robots de software que trabajan 24/7: cargan datos, concilian, generan reportes y conectan tus sistemas sin intervención humana ni errores.',
      desc_eng: 'Software robots that work 24/7: they enter data, reconcile, generate reports and connect your systems with no human intervention and no errors.',
      tags: ['RPA', 'Bots 24/7', 'Integración de sistemas', 'Monitoreo'],
      route: 'robotizacion',
    },
    {
      icon: 'cart',
      name_esp: 'E-Commerce',
      name_eng: 'E-Commerce',
      desc_esp: 'E-commerce personalizados que permiten a las empresas adaptar sus procesos para diferenciarse y vender de forma global.',
      desc_eng: 'Custom e-commerce solutions that allow businesses to adapt their processes to differentiate and sell globally.',
      tags: ['Diseño UX & UI', 'Integraciones', 'CMS personalizado', 'Plataformas a medida'],
      route: 'ecommerce',
    },
    {
      icon: 'code',
      name_esp: 'Webs & Apps',
      name_eng: 'Webs & Apps',
      desc_esp: 'Sitios web, aplicaciones y plataformas personalizadas que ayudan a las empresas a tener impacto global y conectar con su audiencia.',
      desc_eng: 'Custom websites, applications and platforms that help businesses reach global audiences and connect with their users.',
      tags: ['Diseño web', 'Desarrollo web', 'Mobile Apps', 'Webs institucionales'],
      route: 'desarrollo-web',
    },
    {
      icon: 'gears',
      name_esp: 'Automatización',
      name_eng: 'Automation',
      desc_esp: 'Robotizamos procesos con data & analytics predictiva que disminuye errores humanos e incrementa la productividad.',
      desc_eng: 'We automate processes with predictive data & analytics that reduce human error and increase productivity.',
      tags: ['Discovery process', 'Process analytics', 'Testing', 'Implementación'],
      route: 'automatizacion',
    },
    {
      icon: 'brain',
      name_esp: 'Inteligencia Artificial',
      name_eng: 'Artificial Intelligence',
      desc_esp: 'Soluciones de IA que permiten a las empresas tomar decisiones basadas en datos y optimizar sus operaciones.',
      desc_eng: 'AI solutions that enable companies to make data-driven decisions and optimize their operations at scale.',
      tags: ['Modelos predictivos', 'Automatización con IA', 'Análisis avanzado', 'Personalización'],
      route: 'inteligencia-artificial',
    },
  ];

  // ─── Works ──────────────────────────────────────────────────────────────────
  works: WorkItem[] = [
    {
      name: 'DAME MI TICKET',
      desc_esp: 'Servicio de venta de e-tickets para recitales y eventos. Desarrollamos su plataforma de e-commerce completa, con procesos de venta y gestión a medida.',
      desc_eng: 'E-ticket sales service for concerts and events. We built their full e-commerce platform, with custom sales and management processes.',
      tags: ['Robotización', 'E-Commerce', 'Desarrollo web', 'Diseño web', 'Desarrollo de procesos'],
      url: 'https://damemiticket.com',
    },
    {
      name: 'VLU BUS',
      desc_esp: 'Servicio de venta de pasajes para combis y trafics. Desarrollamos su plataforma de e-commerce completa, con procesos de venta integrados.',
      desc_eng: 'Van and shuttle ticket sales service. We built their full e-commerce platform, with integrated sales processes.',
      tags: ['E-Commerce', 'Desarrollo web', 'Diseño web', 'Desarrollo de procesos'],
      url: 'https://vlubus.com.ar',
    },
    {
      name: 'RUBIOLO & ASOCIADOS',
      desc_esp: 'Estudio contable en Rosario. Desarrollamos su software de gestión a medida y rediseñamos sus procesos internos.',
      desc_eng: 'Accounting firm in Rosario. We built their custom management software and redesigned their internal processes.',
      tags: ['Robotización', 'Reingeniería', 'Desarrollo software', 'Desarrollo web'],
      url: 'https://rtm.rubiolo.ar',
    },
    {
      name: 'COLOMBO JUANTO - ESTUDIO JURÍDICO',
      desc_esp: 'Estudio jurídico en Rosario. Desarrollamos su software de gestión de casos a medida y su landing page institucional.',
      desc_eng: 'Law firm in Rosario. We built their custom case management software and their institutional landing page.',
      tags: ['Robotización', 'Desarrollo software', 'Desarrollo y Diseño web'],
      url: 'https://estudio-colombo-juanto.com/',
    },
     {
      name: 'NOVA-DOCS',
      desc_esp: 'Plataforma SaaS de gestión documental para empresas. Desarrollamos el producto de punta a punta, desde el diseño hasta la implementación.',
      desc_eng: 'SaaS platform for enterprise document management. We built the product end to end, from design through implementation.',
      tags: ['Desarrollo software', 'Desarrollo y Diseño web'],
      url: 'https://www.nova-docs.com/',
    },
    {
      name: 'INMOBILIARIA SAN PAULO',
      desc_esp: 'Inmobiliaria en Rufino. Desarrollamos su identidad de marca, su diseño y su sitio web.',
      desc_eng: 'Real estate agency in Rufino. We built their brand identity, design, and website.',
      tags: ['Branding', 'Diseño web', 'Desarrollo web'],
      url: 'https://asp-inmobiliaria.com/',
    },
    {
      name: 'METALFOR',
      desc_esp: 'Fábrica argentina de maquinaria agrícola con más de 45 años de historia. Desarrollamos su sitio web y proyectos internos de software.',
      desc_eng: 'Argentine agricultural machinery manufacturer with 45+ years of history. We built their website and internal software projects.',
      tags: ['Desarrollo web', 'Desarrollo de procesos', 'Proyectos internos'],
      url: 'https://metalfor.com',
    },
    {
      name: 'SOJAS ARGENTINAS',
      desc_esp: 'Servicios para el agro en Marcos Juárez. Desarrollamos su sitio web institucional.',
      desc_eng: 'Agricultural services company in Marcos Juárez. We built their institutional website.',
      tags: ['Desarrollo web', 'Diseño web'],
      url: 'https://sojas.com.ar',
    },
    {
      name: 'VENETA SOLUCIONES',
      desc_esp: 'Venta y alquiler de equipos de impresión. Desarrollamos su plataforma de e-commerce completa.',
      desc_eng: 'Printing equipment sales and rental company. We built their full e-commerce platform.',
      tags: ['E-Commerce', 'Desarrollo web', 'Diseño web'],
      url: 'https://veneta.com.ar/equipos-impresion',
    },
    {
      name: 'BETA ENERGY',
      desc_esp: 'Compañía de servicios energéticos integrados. Desarrollamos su sitio web y diseño corporativo.',
      desc_eng: 'Integrated energy services company. We built their corporate website and design.',
      tags: ['Desarrollo web', 'Diseño web'],
      url: 'https://betanrg.com',
    },
    {
      name: 'GRUPO NANTES',
      desc_esp: 'Asesoramiento integral en tecnología blockchain. Desarrollamos su presencia digital completa.',
      desc_eng: 'Comprehensive blockchain technology consulting. We built their full digital presence.',
      tags: ['Desarrollo web', 'Diseño web'],
      url: 'https://gruponantes.com',
    },
    {
      name: '812 HUB',
      desc_esp: 'Servicios de inclusión laboral. Desarrollamos su sitio web y su diseño.',
      desc_eng: 'Labor market inclusion services. We built their website and design.',
      tags: ['Desarrollo web', 'Diseño web'],
      url: 'https://812hub.com',
    },
    {
      name: 'LOZA ARQUITECTURA',
      desc_esp: 'Estudio de arquitectura en Rosario. Desarrollamos su sitio web institucional.',
      desc_eng: 'Architecture studio in Rosario. We built their institutional website.',
      tags: ['Desarrollo web'],
      url: 'https://lozaarquitectura.com',
    },
    {
      name: 'BROWN ST EQUIPAMIENTO',
      desc_esp: 'Espacio de diseño e interiorismo en Rosario. Desarrollamos su e-commerce con diseño web a medida.',
      desc_eng: 'Interior design and equipment space in Rosario. We built their e-commerce with custom web design.',
      tags: ['E-Commerce', 'Desarrollo web', 'Diseño web'],
      url: 'https://brownst.com.ar',
    },
  ];

  // ─── Preguntas frecuentes ───────────────────────────────────────────────────
  faqItems: FaqItem[] = [
    {
      question_esp: '¿Qué servicios ofrece Patagonia Chain?',
      question_eng: 'What services does Patagonia Chain offer?',
      answer_esp: 'Desarrollamos robotización de procesos, e-commerce, sitios web y aplicaciones a medida, automatización de procesos e inteligencia artificial. Somos un equipo end-to-end: acompañamos desde el diseño hasta la implementación y el go-live.',
      answer_eng: 'We build process robotics, custom e-commerce, websites and applications, process automation, and artificial intelligence solutions. We\'re an end-to-end team: we support you from design through implementation and go-live.',
    },
    {
      question_esp: '¿Cómo es el proceso de trabajo?',
      question_eng: 'What does your work process look like?',
      answer_esp: 'Todo proyecto atraviesa cinco fases: Inicio, Relevamiento, Diseño, Implementación y Cierre y Go-Live. Cada fase tiene entregables claros y un criterio de cierre acordado antes de avanzar a la siguiente.',
      answer_eng: 'Every project goes through five phases: Kickoff, Discovery, Design, Implementation, and Closing & Go-Live. Each phase has clear deliverables and a closing criterion agreed upon before moving to the next.',
    },
    {
      question_esp: '¿Cuánto cuesta un proyecto con Patagonia Chain?',
      question_eng: 'How much does a project with Patagonia Chain cost?',
      answer_esp: 'Cada proyecto es a medida, así que el presupuesto depende del alcance, la complejidad y los plazos. Escribinos y te armamos una propuesta ajustada a tu negocio.',
      answer_eng: 'Every project is custom-built, so the budget depends on scope, complexity, and timeline. Get in touch and we\'ll put together a proposal tailored to your business.',
    },
    {
      question_esp: '¿Dan soporte después de lanzar el proyecto?',
      question_eng: 'Do you provide support after launching the project?',
      answer_esp: 'Sí. La última fase incluye un Documento de Handover que detalla qué se entrega, cómo funciona y cómo se mantiene, para que tu equipo pueda operar con autonomía una vez cerrado el proyecto.',
      answer_eng: 'Yes. The final phase includes a Handover Document detailing what is delivered, how it works, and how it\'s maintained, so your team can operate independently once the project closes.',
    },
    {
      question_esp: '¿Trabajan solo con empresas de Rosario?',
      question_eng: 'Do you only work with companies in Rosario?',
      answer_esp: 'Tenemos base en Rosario, pero trabajamos con clientes de distintas provincias y sectores: agro, estudios profesionales, retail, energía, real estate y más.',
      answer_eng: 'We\'re based in Rosario, but we work with clients across different provinces and industries: agriculture, professional services, retail, energy, real estate, and more.',
    },
  ];

  openFaqIndex = -1;
  activeService = 0;

  // ─── Método de trabajo (teaser) ───────────────────────────────────────────────
  methodSteps = [
    { num: '01', name_esp: 'Inicio',          name_eng: 'Kickoff' },
    { num: '02', name_esp: 'Relevamiento',     name_eng: 'Discovery' },
    { num: '03', name_esp: 'Diseño',           name_eng: 'Design' },
    { num: '04', name_esp: 'Implementación',   name_eng: 'Implementation' },
    { num: '05', name_esp: 'Cierre y Go-Live', name_eng: 'Closing & Go-Live' },
  ];

  // ─── Translations map ───────────────────────────────────────────────────────
  private translations: Record<'ESP' | 'ENG', Translations> = {
    ESP: {
      nav_quienes:   'Quiénes somos',
      nav_servicios: 'Servicios',
      nav_trabajos:  'Trabajos',
      nav_contacto:  'Contacto',
      nav_metodologia: 'Cómo trabajamos',
      hero_location: 'Rosario, Argentina. Desde 2018',
      hero_line1:    'Desarrollo',
      hero_line2:    'de Software',
      hero_desc:     'En Patagonia Chain ayudamos a empresas y emprendimientos a alcanzar sus objetivos. Nos especializamos en que una <strong>inversión mínima</strong> en soluciones digitales se traduzca en un <strong>impacto significativo</strong> en el mundo real.',
      tele_projects: 'PROYECTOS',
      tele_years: 'AÑOS EN LÍNEA',
      tele_mode: 'MODO',
      tele_mode_val: 'ROBOTIZACIÓN',
      cta_services:  'Ver servicios',
      cta_works:     'Nuestros trabajos',
      about_label:   'Quiénes somos',
      about_title:   'Un equipo que construye<br><em>resultados reales</em>',
      about_p1:      'Con base en Rosario, somos un equipo comprometido con la excelencia técnica y el impacto real. Trabajamos junto a clientes inspiradores para crear soluciones que realmente funcionan.',
      about_p2:      'Nos especializamos en traducir <strong>estrategia en código</strong>, procesos en eficiencia, e ideas en productos digitales que generan valor tangible.',
      card1_label:   'Nuestra misión',
      card1_value:   'Impacto real',
      card1_sub:     'Transformamos inversiones mínimas en resultados significativos con soluciones digitales inteligentes y medibles.',
      card2_label:   'Nuestro enfoque',
      card2_value:   'Eficiencia ante todo',
      card2_sub:     'Integramos estrategia y desarrollo en ecosistemas digitales que escalan junto con tu negocio.',
      services_label:'Servicios',
      services_title:'Lo que hacemos',
      services_desc: 'Soluciones digitales end-to-end para empresas que quieren crecer con tecnología.',
      works_label:   'Portfolio',
      works_title:   'Proyectos destacados',
      works_desc:    'Algunos de los proyectos que desarrollamos con nuestros clientes.',
      method_label:  'Cómo trabajamos',
      method_title:  'Un proceso claro en cada etapa',
      method_desc:   'Cinco fases, entregables definidos y criterios de cierre acordados en cada una. Así avanzamos con confianza en todos nuestros proyectos.',
      method_cta:    'Ver metodología completa',
      faq_label:     'Preguntas frecuentes',
      faq_title:     'Lo que más nos preguntan',
      faq_desc:      'Respuestas rápidas antes de escribirnos. Si te queda alguna duda, contactanos.',
      visit_site:    'Visitar sitio',
      visit_more:    'Conocer más',
      cta_label:     '¿Tenés un proyecto?',
      cta_title:     'Hablemos y<br>construyamos <em>juntos</em>',
      cta_email:     'Escribinos',
      footer_sub:    'Desarrollo de Software',
      footer_nav:    'Navegación',
      footer_services: 'Servicios',
      footer_rights: 'Todos los derechos reservados.',
    },
    ENG: {
      nav_quienes:   'About us',
      nav_servicios: 'Services',
      nav_trabajos:  'Works',
      nav_contacto:  'Contact',
      nav_metodologia: 'How we work',
      hero_location: 'Rosario, Argentina — since 2018',
      hero_line1:    'Software',
      hero_line2:    'Development',
      hero_desc:     'At Patagonia Chain we help companies and startups reach their goals. We specialize in turning a <strong>minimal investment</strong> in digital solutions into a <strong>significant real-world impact</strong>.',
      tele_projects: 'PROJECTS',
      tele_years: 'YEARS ONLINE',
      tele_mode: 'MODE',
      tele_mode_val: 'ROBOTICS',
      cta_services:  'See services',
      cta_works:     'Our works',
      about_label:   'About us',
      about_title:   'A team that builds<br><em>real results</em>',
      about_p1:      'Based in Rosario, we are a team committed to technical excellence and real impact. We work alongside inspiring clients to create solutions that actually work.',
      about_p2:      'We specialize in turning <strong>strategy into code</strong>, processes into efficiency, and ideas into digital products that generate tangible value.',
      card1_label:   'Our mission',
      card1_value:   'Real impact',
      card1_sub:     'We transform minimal investments into significant results with intelligent, measurable digital solutions.',
      card2_label:   'Our approach',
      card2_value:   'Efficiency first',
      card2_sub:     'We integrate strategy and development into digital ecosystems that scale alongside your business.',
      services_label:'Services',
      services_title:'What we do',
      services_desc: 'End-to-end digital solutions for companies that want to grow with technology.',
      works_label:   'Portfolio',
      works_title:   'Featured projects',
      works_desc:    'Some of the projects we have built with our clients.',
      method_label:  'How we work',
      method_title:  'A clear process at every step',
      method_desc:   'Five phases, defined deliverables, and closing criteria agreed on at every step. That\'s how we move forward with confidence on every project.',
      method_cta:    'See the full methodology',
      faq_label:     'FAQ',
      faq_title:     'Frequently asked questions',
      faq_desc:      'Quick answers before you reach out. Still have a question? Contact us.',
      visit_site:    'Visit site',
      visit_more:    'Learn more',
      cta_label:     'Have a project?',
      cta_title:     "Let's talk and<br>build <em>together</em>",
      cta_email:     'Get in touch',
      footer_sub:    'Software Development',
      footer_nav:    'Navigation',
      footer_services: 'Services',
      footer_rights: 'All rights reserved.',
    },
  };

  // ─── Lifecycle ──────────────────────────────────────────────────────────────
  constructor(
    private router: Router,
    private title: Title,
    private meta: Meta,
    private host: ElementRef,
    private seoService: SeoService,
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit(): void {

    let saved: 'ESP' | 'ENG' | null = null;
    try { saved = sessionStorage.getItem('idioma') as 'ESP' | 'ENG' | null; } catch {}
    this.lang = saved ?? 'ESP';
    this.applyLang();
    try { this.svgBase = location.pathname + location.search; } catch {}

    this.title.setTitle(' Patagonia Chain | Desarrollo de Software en Rosario | Software factory');
    this.meta.updateTag({ name: 'description', content: 'Agencia de desarrollo de software en Rosario, Argentina. Desarrollo web a medida, e-commerce, automatización de procesos e inteligencia artificial para empresas que quieren crecer con tecnología.' });
    this.meta.updateTag({ property: 'og:title', content: 'Patagonia Chain | Desarrollo de Software en Rosario | Software factory' });
    this.meta.updateTag({ property: 'og:description', content: 'Agencia de desarrollo de software en Rosario, Argentina. Desarrollo web a medida, e-commerce, automatización e inteligencia artificial.' });

    setTimeout(() => this.posicion(), 200);
  }

  ngAfterViewInit(): void {
    // Animaciones que usan window: solo en el navegador (no en el prerender SSR)
    if (isPlatformBrowser(this.platformId)) {
      this.startCircuit();
      this.startHeadTracking();
      this.decodeTitle();
    }
  }

  // ─── Título del hero que se "decodifica": glifos al azar que se fijan ─────
  // Escribe sobre los mismos nodos de texto que maneja Angular (nodeValue), así
  // un cambio de idioma posterior sigue actualizando el título normalmente.
  private decodeRafs = new Set<number>();

  private decodeTitle(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const root: HTMLElement = this.host.nativeElement;
    const nodes = ['.ht-l1', '.ht-l2']
      .map(sel => root.querySelector(sel)?.firstChild)
      .filter((n): n is ChildNode => !!n && n.nodeType === Node.TEXT_NODE);
    this.decodeNodes(nodes, 1400, 320);

  }

  private decodeNodes(nodes: ChildNode[], dur: number, stagger: number): void {
    if (!nodes.length) return;
    const finals = nodes.map(n => n.nodeValue || '');
    const glyphs = '01<>/\\|#%&*+=_ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let start = 0;
    let raf = 0;

    this.zone.runOutsideAngular(() => {
      const frame = (ts: number) => {
        this.decodeRafs.delete(raf);
        if (!start) start = ts;
        let done = true;
        nodes.forEach((node, li) => {
          const final = finals[li];
          const p = Math.min(1, Math.max(0, (ts - start - li * stagger) / dur));
          const fixed = Math.floor(final.length * p);
          if (p < 1) done = false;
          node.nodeValue = final.split('').map((ch, i) =>
            i < fixed || ch.trim() === '' ? ch : glyphs[Math.floor(Math.random() * glyphs.length)]
          ).join('');
        });
        if (!done) { raf = requestAnimationFrame(frame); this.decodeRafs.add(raf); }
      };
      raf = requestAnimationFrame(frame);
      this.decodeRafs.add(raf);
    });
  }
  // ─── Mirada del robot: cabecea (solo arriba/abajo) hacia el mouse ──────────
  // Pivota sobre el cuello con un tope de ±9° y se acerca al objetivo de a
  // poco (como un servo), así nunca gira hacia el usuario ni se desarma.
  // Solo escucha dentro del hero; al salir vuelve lentamente a reposo.
  private headEl: SVGGElement | null = null;
  private robotSvg: SVGSVGElement | null = null;
  private heroEl: HTMLElement | null = null;
  private headAngle = 0;
  private headTarget = 0;
  private headRaf = 0;
  private readonly headMaxDeg = 9;

  private onHeroMove = (e: MouseEvent) => {
    if (!this.robotSvg) return;
    const r = this.robotSvg.getBoundingClientRect();
    // Ojo del visor en coordenadas del viewBox (90, 66) de 260x262
    const eyeX = r.left + r.width * (90 / 260);
    const eyeY = r.top + r.height * (66 / 262);
    const dy = e.clientY - eyeY;
    // Mira hacia la izquierda: si el mouse pasa a la derecha se toma una
    // distancia mínima, para que no cabecee de golpe al cruzarlo.
    const dx = Math.max(eyeX - e.clientX, 160);
    const deg = -Math.atan2(dy, dx) * 180 / Math.PI * 0.45;
    this.headTarget = Math.max(-this.headMaxDeg, Math.min(this.headMaxDeg, deg));
    this.kickHead();
  };

  private onHeroLeave = () => {
    this.headTarget = 0;
    this.kickHead();
  };

  private startHeadTracking(): void {
    const root: HTMLElement = this.host.nativeElement;
    this.headEl = root.querySelector<SVGGElement>('.rb-cabeza');
    this.robotSvg = root.querySelector<SVGSVGElement>('.robot-svg');
    this.heroEl = root.querySelector<HTMLElement>('.hero');
    if (!this.headEl || !this.robotSvg || !this.heroEl) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(hover: hover)').matches) return;

    // Fuera de la zona de Angular: mousemove no dispara detección de cambios
    this.zone.runOutsideAngular(() => {
      this.heroEl!.addEventListener('mousemove', this.onHeroMove);
      this.heroEl!.addEventListener('mouseleave', this.onHeroLeave);
    });
  }

  private kickHead(): void {
    if (this.headRaf) return;
    const step = () => {
      const diff = this.headTarget - this.headAngle;
      if (Math.abs(diff) < 0.02) {
        this.headAngle = this.headTarget;
        this.headRaf = 0;
      } else {
        this.headAngle += diff * 0.035;
        this.headRaf = requestAnimationFrame(step);
      }
      this.headEl!.style.transform = `rotate(${this.headAngle.toFixed(3)}deg)`;
    };
    this.headRaf = requestAnimationFrame(step);
  }

  // ─── url(#id) del SVG del robot, absoluta respecto de la ruta actual ────────
  u(id: string): string {
    return `url(${this.svgBase}#${id})`;
  }

  ngOnDestroy(): void {
    if (this.circuitRaf) cancelAnimationFrame(this.circuitRaf);
    if (this.headRaf) cancelAnimationFrame(this.headRaf);
    this.decodeRafs.forEach(id => cancelAnimationFrame(id));
    this.heroEl?.removeEventListener('mousemove', this.onHeroMove);
    this.heroEl?.removeEventListener('mouseleave', this.onHeroLeave);
    window.removeEventListener('resize', this.circuitResizeHandler);
    this.circuitResizeObs?.disconnect();
    this.seoService.removeFaqSchema();
  }

  // Detecta cambios de idioma hechos desde el header compartido (si lo hubiera)
  ngDoCheck(): void {
    try {
      const current = sessionStorage.getItem('idioma') as 'ESP' | 'ENG' | null;
      const next = current ?? 'ESP';
      if (next !== this.lang) {
        this.lang = next;
        this.applyLang();
      }
    } catch {}
  }

  // ─── Language ───────────────────────────────────────────────────────────────
  setLang(lang: 'ESP' | 'ENG'): void {
    this.lang = lang;
    try { sessionStorage.setItem('idioma', lang); } catch {}
    this.applyLang();
  }

  private applyLang(): void {
    this.t = this.translations[this.lang];
    this.marqueeItems = this.lang === 'ENG'
      ? ['Robotics', 'Web Development', 'E-Commerce', 'Custom Software', 'Automation', 'Artificial Intelligence', 'Mobile Apps', 'UX & UI Design', 'Rosario, Argentina']
      : ['Robotización', 'Desarrollo Web', 'E-Commerce', 'Software a Medida', 'Automatización', 'Inteligencia Artificial', 'Mobile Apps', 'UX & UI Design', 'Rosario, Argentina'];

    this.seoService.setFaqSchema(this.faqItems.map(f => ({
      question: this.lang === 'ESP' ? f.question_esp : f.question_eng,
      answer: this.lang === 'ESP' ? f.answer_esp : f.answer_eng,
    })));

    // El nuevo idioma cambia el alto real de .about-head / .about-module
    // (textos de distinto largo), y measureCircuitZones() solo se
    // recalculaba en resize: sin esto, el circuito queda dibujado con las
    // coordenadas del idioma anterior. Se difiere un tick para medir el
    // DOM ya actualizado por Angular.
    if (this.circuitCanvas) {
      setTimeout(() => this.sizeCircuitCanvas(), 0);
    }
  }

  // ─── FAQ accordion ──────────────────────────────────────────────────────────
  toggleFaq(i: number): void {
    this.openFaqIndex = this.openFaqIndex === i ? -1 : i;
  }

  // ─── Índice interactivo de servicios ────────────────────────────────────────
  setActiveService(i: number): void {
    this.activeService = i;
  }

  // ─── Circuito animado de fondo de "Quiénes somos" ───────────────────────────
  // Estructura "contorno": un bracket abraza el borde real de cada tarjeta
  // (arriba, afuera y abajo), con una fuga corta hacia el margen y un enlace
  // vertical entre ambas tarjetas. El circuito queda pegado a los componentes
  // en vez de flotar como una geometría aparte. Coordenadas recalculadas
  // contra el DOM real en cada resize.
  private circuitPaths: number[][][] = [];
  private circuitRoles: string[] = [];
  private rawModuleRects: CircuitZone[] = [];
  private readonly circuitBracketOffset = 10;
  private circuitCanvas: HTMLCanvasElement | null = null;
  // (la versión de TS del proyecto no trae los tipos de ResizeObserver)
  private circuitResizeObs: { observe(el: Element): void; disconnect(): void } | null = null;
  private circuitCtx: CanvasRenderingContext2D | null = null;
  private circuitRoot: HTMLElement | null = null;
  private circuitReduced = false;
  private circuitStart = 0;
  private muteZones: CircuitZone[] = [];
  private diffuseZones: CircuitZone[] = [];

  private startCircuit(): void {
    const root: HTMLElement = this.host.nativeElement;
    const canvas = root.querySelector<HTMLCanvasElement>('.about-canvas');
    const ctx = canvas?.getContext('2d') ?? null;
    if (!canvas || !ctx) return;

    this.circuitRoot = root;
    this.circuitCanvas = canvas;
    this.circuitCtx = ctx;
    this.circuitReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.sizeCircuitCanvas();
    window.addEventListener('resize', this.circuitResizeHandler);
    // El circuito abraza las tarjetas: hay que volver a medir cuando cambia su
    // tamaño sin que cambie la ventana (carga de las fuentes, cambio de idioma).
    const RO = (window as any).ResizeObserver;
    if (RO) {
      this.circuitResizeObs = new RO(() => this.sizeCircuitCanvas());
      this.circuitResizeObs.observe(canvas.parentElement!);
    }
    (this.document as any).fonts?.ready?.then(() => this.sizeCircuitCanvas());

    this.drawCircuitStatic();
    if (this.circuitReduced) return;

    const tick = (ts: number) => {
      if (!this.circuitStart) this.circuitStart = ts;
      this.drawCircuitFrame((ts - this.circuitStart) / 1000);
      this.circuitRaf = requestAnimationFrame(tick);
    };
    this.circuitRaf = requestAnimationFrame(tick);
  }

  private sizeCircuitCanvas(): void {
    if (!this.circuitCanvas || !this.circuitCtx) return;
    const rect = this.circuitCanvas.parentElement!.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.circuitCanvas.width = rect.width * dpr;
    this.circuitCanvas.height = rect.height * dpr;
    this.circuitCanvas.style.width = rect.width + 'px';
    this.circuitCanvas.style.height = rect.height + 'px';
    this.circuitCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.measureCircuitZones();
    if (this.circuitReduced) this.drawCircuitStatic();
  }

  // Convierte elementos reales del DOM a rectángulos en el espacio del canvas,
  // con un margen de "colchón" para que el apagado/difuminado no sea abrupto.
  private measureCircuitZones(): void {
    if (!this.circuitCanvas || !this.circuitRoot) return;
    const canvasRect = this.circuitCanvas.getBoundingClientRect();
    const toRect = (el: Element | null, margin: number) => {
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return {
        x0: r.left - canvasRect.left - margin,
        y0: r.top - canvasRect.top - margin,
        x1: r.right - canvasRect.left + margin,
        y1: r.bottom - canvasRect.top + margin,
      };
    };

    const head = toRect(this.circuitRoot.querySelector('.about-head'), 36);
    const telemetry = toRect(this.circuitRoot.querySelector('.about-telemetry'), 24);
    this.muteZones = [head, telemetry].filter((z): z is CircuitZone => !!z);

    const modules = Array.from(this.circuitRoot.querySelectorAll('.about-module'));
    this.rawModuleRects = modules
      .map(el => toRect(el, 0))
      .filter((z): z is CircuitZone => !!z);
    this.diffuseZones = modules
      .map(el => toRect(el, 14))
      .filter((z): z is CircuitZone => !!z);

    this.buildCircuitPaths(this.circuitCanvas.clientWidth, this.circuitCanvas.clientHeight);
  }

  // Arma el trazado en espacio de píxeles y lo normaliza a 0..1 para que
  // circuitPointAt() lo escale igual que antes. Cada path lleva un "rol"
  // (slow/mid/fast) que define su velocidad y color al animar.
  private buildCircuitPaths(w: number, h: number): void {
    if (!w || !h) { this.circuitPaths = []; this.circuitRoles = []; return; }
    const off = this.circuitBracketOffset;
    const paths: number[][][] = [];
    const roles: string[] = [];

    if (this.rawModuleRects.length >= 2) {
      this.rawModuleRects.forEach(c => {
        const x0 = c.x0 - off, y0 = c.y0 - off, x1 = c.x1 + off, y1 = c.y1 + off;
        const midX = (x0 + x1) / 2;
        paths.push([[midX, y0], [x1, y0], [x1, y1], [midX, y1]]);
        roles.push('slow');
        paths.push([[x1, (y0 + y1) / 2], [x1 + 40, (y0 + y1) / 2]]);
        roles.push('fast');
      });
      const c1 = this.rawModuleRects[0], c2 = this.rawModuleRects[1];
      const linkX = c1.x0 + (c1.x1 - c1.x0) / 2;
      paths.push([[linkX, c1.y1 + off], [linkX, c2.y0 - off]]);
      roles.push('mid');
    }

    this.circuitPaths = paths.map(p => p.map(([x, y]) => [x / w, y / h]));
    this.circuitRoles = roles;
  }

  // 1 = totalmente dentro del rect, 0 = a `margin` px o más afuera, con transición suave.
  private zoneFactor(zone: CircuitZone, x: number, y: number, margin: number): number {
    const dx = x < zone.x0 ? zone.x0 - x : (x > zone.x1 ? x - zone.x1 : 0);
    const dy = y < zone.y0 ? zone.y0 - y : (y > zone.y1 ? y - zone.y1 : 0);
    const d = Math.max(dx, dy);
    return Math.max(0, Math.min(1, 1 - d / margin));
  }

  private circuitAlphaAt(x: number, y: number): { alpha: number; blur: number } {
    let alpha = 1;
    let blur = 0;
    this.muteZones.forEach(z => { alpha *= 1 - this.zoneFactor(z, x, y, 40); });
    this.diffuseZones.forEach(z => {
      const f = this.zoneFactor(z, x, y, 30);
      alpha *= 1 - f * 0.72;
      blur += f * 10;
    });
    return { alpha, blur };
  }

  private circuitPointAt(path: number[][], t: number): number[] {
    const segs = path.length - 1;
    const f = t * segs;
    const i = Math.min(Math.floor(f), segs - 1);
    const lf = f - i;
    const a = path[i], b = path[i + 1];
    return [a[0] + (b[0] - a[0]) * lf, a[1] + (b[1] - a[1]) * lf];
  }

  private drawCircuitStatic(): void {
    const ctx = this.circuitCtx, canvas = this.circuitCanvas;
    if (!ctx || !canvas) return;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    ctx.clearRect(0, 0, w, h);

    // Traza continua: cada segmento hereda la opacidad promedio de sus dos
    // extremos, así se ve como una línea viva (no puntos sueltos) que se
    // apaga sobre el texto y se atenúa/difumina bajo las tarjetas.
    const STEPS = 90;
    ctx.lineCap = 'round';
    this.circuitPaths.forEach(path => {
      let prev = this.circuitPointAt(path, 0);
      let prevState = this.circuitAlphaAt(prev[0] * w, prev[1] * h);
      for (let s = 1; s <= STEPS; s++) {
        const cur = this.circuitPointAt(path, s / STEPS);
        const curState = this.circuitAlphaAt(cur[0] * w, cur[1] * h);
        const avgAlpha = (prevState.alpha + curState.alpha) / 2;
        if (avgAlpha > 0.02) {
          const avgBlur = (prevState.blur + curState.blur) / 2;
          ctx.beginPath();
          ctx.moveTo(prev[0] * w, prev[1] * h);
          ctx.lineTo(cur[0] * w, cur[1] * h);
          ctx.strokeStyle = `rgba(150,165,180,${0.55 * avgAlpha})`;
          ctx.lineWidth = 1.4;
          ctx.shadowColor = 'rgba(150,165,180,0.6)';
          ctx.shadowBlur = avgBlur * 0.6;
          ctx.stroke();
        }
        prev = cur;
        prevState = curState;
      }
      // "vías" en los vértices del trazado
      path.forEach(pt => {
        const x = pt[0] * w, y = pt[1] * h;
        const { alpha, blur } = this.circuitAlphaAt(x, y);
        if (alpha <= 0.02) return;
        ctx.beginPath();
        ctx.arc(x, y, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(150,165,180,${0.5 * alpha})`;
        ctx.shadowColor = 'rgba(150,165,180,0.7)';
        ctx.shadowBlur = 4 + blur * 0.6;
        ctx.fill();
      });
    });
    ctx.shadowBlur = 0;
  }

  private drawCircuitFrame(elapsed: number): void {
    const ctx = this.circuitCtx, canvas = this.circuitCanvas;
    if (!ctx || !canvas) return;
    this.drawCircuitStatic();
    const w = canvas.clientWidth, h = canvas.clientHeight;
    // slow = bracket alrededor de la tarjeta (ambiental)
    // mid  = enlace entre las dos tarjetas · fast = fuga hacia el margen
    const speedByRole: Record<string, number> = { slow: 0.05, mid: 0.12, fast: 0.4 };
    this.circuitPaths.forEach((path, idx) => {
      const role = this.circuitRoles[idx] || 'mid';
      const speed = speedByRole[role];
      const t = (elapsed * speed + idx * 0.29) % 1;
      const [px, py] = this.circuitPointAt(path, t);
      const x = px * w, y = py * h;
      const { alpha, blur } = this.circuitAlphaAt(x, y);
      if (alpha <= 0.02) return;
      const color = role === 'fast' ? '255,45,31' : '150,165,180';
      ctx.beginPath();
      ctx.arc(x, y, 3.2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${color},${0.9 * alpha})`;
      ctx.shadowColor = `rgba(${color},${0.8 * alpha})`;
      ctx.shadowBlur = 8 + blur;
      ctx.fill();
      ctx.shadowBlur = 0;
    });
  }

  // ─── Scroll / navegación ────────────────────────────────────────────────────
  scrollTo(id: string): void {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  goMethod(): void {
    this.router.navigate(['/como-trabajamos']);
  }

  private posicion(): void {
    const id = localStorage.getItem('pos');
    if (!id) return;
    const map: Record<string, string> = {
      pat: 'hero', somos: 'quienes', serv: 'servicios',
      footer: 'footer', proyy: 'trabajos', con: 'contacto',
      hero: 'hero', quienes: 'quienes', servicios: 'servicios',
      trabajos: 'trabajos', contacto: 'contacto',
    };
    const target = map[id];
    if (target) this.scrollTo(target);
    localStorage.removeItem('pos');
  }

  ir(url: string): void {
    this.router.navigate(['/servicio/' + url]);
  }

  yendo(url: string): void {
    window.open(url, '_blank');
  }

  pad(i: number): string {
    const n = i + 1;
    return n < 10 ? '0' + n : String(n);
  }
}
